/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useApolloClient } from '@apollo/client'
import {
  SABRE_GET_SIMPLE_PRODUCTS,
  type SabreGetSimpleProductsQuery,
  type SabreGetSimpleProductsQueryVariables,
} from '@dai/graphql'
import React from 'react'

import {
  collectVisibleUpcs,
  normalizeUpcKey,
  type ExplorerProduct,
  type LabelingExplorerImage,
  type LabelingExplorerItem,
} from './explorerUtils'
import { PRODUCT_LOOKUP_BATCH_SIZE } from './explorerUrlParams'

export const useVisibleProductMap = (
  items: LabelingExplorerItem[],
  selectedImage: LabelingExplorerImage | null,
) => {
  const apolloClient = useApolloClient()
  const [productsByUpc, setProductsByUpc] = React.useState<
    Record<string, ExplorerProduct>
  >({})
  const completedProductLookupUpcs = React.useRef(new Set<string>())
  const inFlightProductLookupUpcs = React.useRef(new Set<string>())
  const productsByUpcRef = React.useRef(productsByUpc)
  const visibleUpcs = React.useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...collectVisibleUpcs(items),
            ...((selectedImage?.boundingBoxes ?? []).flatMap(box => [
              box.verifiedUpc,
              box.currentUpc,
              ...box.predictedUpcs.map(prediction => prediction.upc),
            ]) as Array<string | null>),
          ].filter((upc): upc is string => Boolean(upc)),
        ),
      ),
    [items, selectedImage],
  )
  const visibleUpcsKey = visibleUpcs.join('|')

  React.useEffect(() => {
    productsByUpcRef.current = productsByUpc
  }, [productsByUpc])

  React.useEffect(() => {
    const missingUpcs = visibleUpcs.filter(upc => {
      const upcKey = normalizeUpcKey(upc)
      return (
        upcKey &&
        !productsByUpcRef.current[upcKey] &&
        !completedProductLookupUpcs.current.has(upcKey) &&
        !inFlightProductLookupUpcs.current.has(upcKey)
      )
    })
    const missingUpcKeys = Array.from(
      new Set(
        missingUpcs
          .map(upc => normalizeUpcKey(upc))
          .filter((upc): upc is string => Boolean(upc)),
      ),
    )
    if (missingUpcKeys.length === 0) return

    let active = true
    const batches = Array.from(
      {
        length: Math.ceil(missingUpcKeys.length / PRODUCT_LOOKUP_BATCH_SIZE),
      },
      (_, index) =>
        missingUpcKeys.slice(
          index * PRODUCT_LOOKUP_BATCH_SIZE,
          (index + 1) * PRODUCT_LOOKUP_BATCH_SIZE,
        ),
    )
    batches.flat().forEach(upc => inFlightProductLookupUpcs.current.add(upc))

    void Promise.all(
      batches.map(batch =>
        apolloClient
          .query<
            SabreGetSimpleProductsQuery,
            SabreGetSimpleProductsQueryVariables
          >({
            query: SABRE_GET_SIMPLE_PRODUCTS,
            context: { endPoint: 'sabre' },
            variables: { input: { upcs: batch, limit: batch.length } },
            fetchPolicy: 'network-only',
          })
          .then(result => {
            batch.forEach(upc => {
              inFlightProductLookupUpcs.current.delete(upc)
              completedProductLookupUpcs.current.add(upc)
            })
            return result.data.products.results
          })
          .catch(() => {
            batch.forEach(upc => inFlightProductLookupUpcs.current.delete(upc))
            return []
          }),
      ),
    ).then(resultsByBatch => {
      if (!active) return
      setProductsByUpc(current => {
        const next = { ...current }
        let changed = false
        resultsByBatch.flat().forEach(product => {
          const upcKey = normalizeUpcKey(product.upcA)
          if (!upcKey || current[upcKey] === product) return
          next[upcKey] = product
          changed = true
        })
        return changed ? next : current
      })
    })

    return () => {
      active = false
      batches.flat().forEach(upc => {
        if (!completedProductLookupUpcs.current.has(upc)) {
          inFlightProductLookupUpcs.current.delete(upc)
        }
      })
    }
  }, [apolloClient, visibleUpcs, visibleUpcsKey])

  return productsByUpc
}
